import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, X, PartyPopper } from "lucide-react";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { useApp } from "../context/AppContext";
import { useS, prefillAnswers, recommend } from "../lib/simple";
import { QUESTIONS, StarterQuestion } from "../components/starter/StarterQuestions";
import { StarterResult } from "../components/starter/StarterResult";
import { StarterCreate } from "../components/starter/StarterCreate";
import { GoFurther } from "../components/starter/GoFurther";

const initialStep = (st, edit) => {
  if (edit || !st.answers) return 0;
  if (st.status === "draft") return "create";
  if (st.status === "proposed") return "result";
  return 0;
};

export default function StartGuide() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { data, setData } = useApp();
  const { s, lang } = useS();
  const st = data?.starter || {};
  const [step, setStep] = useState(() => initialStep(st, params.get("edit")));
  const [answers, setAnswers] = useState(() => st.answers || prefillAnswers(data?.onboarding));
  const action = recommend(answers, lang);

  const update = (patch) => setData((d) => ({ ...d, starter: { ...(d?.starter || {}), ...patch, updatedAt: Date.now() } }));
  const isQ = typeof step === "number";
  const q = isQ ? QUESTIONS[step] : null;
  const answered = q && (q.multi ? (answers[q.key] || []).length > 0 : !!answers[q.key]);

  const next = () => {
    if (step < QUESTIONS.length - 1) return setStep(step + 1);
    const a = recommend(answers, lang);
    const same = st.actionId === a.id && st.status !== "done";
    update({ answers, actionId: a.id, status: same ? st.status : "proposed", draftId: same ? st.draftId : null, dismissed: false });
    setStep("result");
  };
  const back = () => (step === "create" ? setStep("result") : step === "result" ? setStep(QUESTIONS.length - 1) : step > 0 && setStep(step - 1));
  const skip = () => { update({ dismissed: true }); nav("/app/dashboard"); };
  const startAction = () => {
    if (action.kind === "results") { update({ status: "done", doneAt: Date.now() }); return nav("/app/results"); }
    setStep("create");
  };
  const published = () => {
    update({ status: "done", doneAt: Date.now() });
    setData((d) => ({ ...d, recent: [{ text: s("recent_done", { t: action.title }), time: s("just_now"), type: "mission" }, ...(d?.recent || [])].slice(0, 8) }));
    setStep("done");
  };
  const progress = isQ ? ((step + 1) / (QUESTIONS.length + 1)) * 100 : 100;

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col bg-[var(--lb-bg)]" data-testid="start-guide">
      <div className="absolute inset-0 aurora opacity-60 pointer-events-none" />
      <div className="relative z-10 max-w-lg mx-auto w-full px-5 pt-5 pb-8 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-5">
          <Logo />
          <button onClick={step === "done" ? () => nav("/app/dashboard") : skip} data-testid="starter-skip"
            className="inline-flex items-center gap-1 min-h-[44px] px-3 rounded-full text-sm text-[hsl(var(--muted-foreground))] hover:text-white">
            {step === "done" ? <X size={18} /> : <>{s("skip")} <X size={15} /></>}
          </button>
        </div>
        <div className="mb-7">
          {isQ && <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2" data-testid="starter-count">{s("q_count", { i: step + 1, n: QUESTIONS.length })}</p>}
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <motion.div className="h-full bg-gradient-to-r from-primary to-neon" animate={{ width: `${progress}%` }} />
          </div>
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={String(step)} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.22 }} className="flex-1">
            {isQ && <StarterQuestion q={q} answers={answers} onChange={(k, v) => setAnswers((p) => ({ ...p, [k]: v }))} />}
            {step === "result" && action && <StarterResult action={action} time={answers.time} onStart={startAction} onEdit={() => setStep(0)} />}
            {step === "create" && action && (
              <StarterCreate action={action} answers={answers} draftId={st.draftId}
                onDraft={(id) => update({ draftId: id, status: "draft" })} onPublished={published} />
            )}
            {step === "done" && (
              <div className="space-y-5" data-testid="starter-done">
                <div className="text-center pt-4">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center"><PartyPopper size={30} /></div>
                  <h1 className="font-display font-black text-2xl mt-4">{s("done_title")}</h1>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{s("done_sub")}</p>
                </div>
                <GoFurther onLater={() => { update({ diagLater: true }); nav("/app/dashboard"); }} />
                <Button onClick={() => nav("/app/dashboard")} data-testid="starter-go-home" variant="outline" className="w-full rounded-full h-12 border-white/10 bg-white/5">{s("go_home")}</Button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        {(isQ || step === "create") && (
          <div className="flex gap-3 mt-8">
            {(step !== 0) && (
              <Button onClick={back} data-testid="starter-back" variant="outline" aria-label={s("back")} className="rounded-full h-14 w-14 border-white/10 bg-white/5"><ArrowLeft size={18} /></Button>
            )}
            {isQ && (
              <Button onClick={next} disabled={!answered} data-testid="starter-next" className="flex-1 rounded-full h-14 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold text-base">
                {step === QUESTIONS.length - 1 ? s("see_action") : s("next")} <ArrowRight size={18} className="ml-1.5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
